import React from 'react';
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Candidate from '../Candidate';
import Api from '../API';

jest.mock('../API');
const mockApi = Api as jest.Mocked<typeof Api>;

jest.mock('../../Components/CustomUIElements/StyledSnackbar', () => {
  return function MockStyledSnackbar({ message, state }: { message: string; state: string }) {
    return (
      <div data-testid="snackbar" data-state={state}>
        {message}
      </div>
    );
  };
});

jest.mock('../../Components/Utilities/formatUserPermissions', () => {
  return {
    formatUserPermissions: () => ({
      Candidates: {
        'Edit Candidates': true
      }
    })
  };
});

jest.mock('../ProfileTab', () => {
  return function MockProfileTab({ candidate }: { candidate: any }) {
    return (
      <div data-testid="profile-tab">
        Profile Tab - {candidate?.firstname} {candidate?.lastname}
      </div>
    );
  };
});

jest.mock('../ActivitiesTab', () => {
  return function MockActivitiesTab({ candidateId }: { candidateId: number }) {
    return <div data-testid="activities-tab">Activities Tab - Candidate {candidateId}</div>;
  };
});

jest.mock('../../Candidates/EditCandidate', () => {
  return function MockEditCandidate({
    editedCandidate,
    CandidateState
  }: {
    editedCandidate: any;
    CandidateState: any;
  }) {
    return CandidateState.openEditModal ? (
      <div data-testid="edit-candidate-modal">
        Editing {editedCandidate?.firstname} {editedCandidate?.lastname}
      </div>
    ) : null;
  };
});

jest.mock('../../Candidates/DeleteCandidate', () => {
  return function MockDeleteCandidate({
    isDialogOpen,
    candidateId
  }: {
    isDialogOpen: boolean;
    candidateId: number;
  }) {
    return isDialogOpen ? (
      <div data-testid="delete-candidate-modal">Deleting candidate {candidateId}</div>
    ) : null;
  };
});

jest.mock('../MergeCandidates', () => {
  return function MockMergeCandidates({
    isOpen,
    candidateId
  }: {
    isOpen: boolean;
    candidateId: number;
  }) {
    return isOpen ? (
      <div data-testid="merge-candidates-modal">Merging candidate {candidateId}</div>
    ) : null;
  };
});

const createTestQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: false
      }
    }
  });

const theme = createTheme();

const renderWithProviders = (component: React.ReactElement) => {
  const queryClient = createTestQueryClient();
  return render(
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>{component}</ThemeProvider>
    </QueryClientProvider>
  );
};

describe('Candidate Component', () => {
  beforeEach(() => {
    jest.clearAllMocks();
  });

  const mockCandidateData = {
    id: 1,
    firstname: 'John',
    lastname: 'Doe',
    email: 'john.doe@example.com',
    phone1: '1234567890',
    phone2: '',
    title: 'Mr',
    gender: 'm',
    employee_id: null,
    linkedin_url: null,
    duplicated_candidates: []
  };

  it('renders the candidate page with loading skeleton initially', () => {
    mockApi.getCandidateData.mockImplementation(() => new Promise(() => {}));

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    const skeleton = container.querySelector('.MuiSkeleton-root');
    expect(skeleton).toBeInTheDocument();
  });

  it('displays candidate name when loaded', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      expect(screen.getByText('John Doe')).toBeInTheDocument();
    });
  });

  it('displays profile tab by default', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      expect(screen.getByTestId('profile-tab')).toBeInTheDocument();
      expect(screen.getByText('Profile Tab - John Doe')).toBeInTheDocument();
    });
  });

  it('switches to activities tab when clicked', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      expect(screen.getByTestId('profile-tab')).toBeInTheDocument();
    });

    const activitiesTab = screen.getByText('Activities');
    fireEvent.click(activitiesTab);

    await waitFor(() => {
      expect(screen.getByTestId('activities-tab')).toBeInTheDocument();
      expect(screen.queryByTestId('profile-tab')).not.toBeInTheDocument();
    });
  });

  it('shows edit button when user has edit permissions', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      const editButton = container.querySelector('#edit-candidate-button');
      expect(editButton).toBeInTheDocument();
    });
  });

  it('shows delete button when user is admin', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      const deleteButton = container.querySelector('#delete-candidate-button');
      expect(deleteButton).toBeInTheDocument();
    });
  });

  it('hides delete button when user is not admin', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={false} />);

    await waitFor(() => {
      expect(container.querySelector('#delete-candidate-button')).not.toBeInTheDocument();
    });
  });

  it('opens edit modal when edit button is clicked', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      const editButton = container.querySelector('#edit-candidate-button');
      expect(editButton).toBeInTheDocument();
      fireEvent.click(editButton!);
    });

    await waitFor(() => {
      expect(screen.getByTestId('edit-candidate-modal')).toBeInTheDocument();
      expect(screen.getByText('Editing John Doe')).toBeInTheDocument();
    });
  });

  it('opens delete modal when delete button is clicked', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      const deleteButton = container.querySelector('#delete-candidate-button');
      expect(deleteButton).toBeInTheDocument();
      fireEvent.click(deleteButton!);
    });

    await waitFor(() => {
      expect(screen.getByTestId('delete-candidate-modal')).toBeInTheDocument();
      expect(screen.getByText('Deleting candidate 1')).toBeInTheDocument();
    });
  });

  it('shows merge candidates icon when duplicated candidates exist', async () => {
    const candidateWithDuplicates = {
      ...mockCandidateData,
      duplicated_candidates: [
        {
          id: 1,
          name: 'John Doe',
          email: 'john.doe@example.com',
          phone: '1234567890',
          address1: [],
          address2: [],
          created_at: '2023-01-01'
        },
        {
          id: 2,
          name: 'John Doe',
          email: 'john.doe@example.com',
          phone: '1234567890',
          address1: [],
          address2: [],
          created_at: '2023-01-01'
        }
      ]
    };

    mockApi.getCandidateData.mockResolvedValue({ res: candidateWithDuplicates });

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      const mergeButton = container.querySelector('#duplicated-candidate-button');
      expect(mergeButton).toBeInTheDocument();
    });
  });

  it('opens merge modal when merge button is clicked', async () => {
    const candidateWithDuplicates = {
      ...mockCandidateData,
      duplicated_candidates: [
        {
          id: 1,
          name: 'John Doe',
          email: 'john.doe@example.com',
          phone: '1234567890',
          address1: [],
          address2: [],
          created_at: '2023-01-01'
        },
        {
          id: 2,
          name: 'John Doe',
          email: 'john.doe@example.com',
          phone: '1234567890',
          address1: [],
          address2: [],
          created_at: '2023-01-01'
        }
      ]
    };

    mockApi.getCandidateData.mockResolvedValue({ res: candidateWithDuplicates });

    const { container } = renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      const mergeButton = container.querySelector('#duplicated-candidate-button');
      expect(mergeButton).toBeInTheDocument();
      fireEvent.click(mergeButton!);
    });

    await waitFor(() => {
      expect(screen.getByTestId('merge-candidates-modal')).toBeInTheDocument();
    });
  });

  it('handles API errors gracefully', async () => {
    const consoleError = jest.spyOn(console, 'error').mockImplementation(() => undefined);
    mockApi.getCandidateData.mockRejectedValue(new Error('API Error'));

    renderWithProviders(<Candidate candidateId={1} isAdminUser={true} />);

    await waitFor(() => {
      expect(screen.getByTestId('snackbar')).toHaveAttribute('data-state', 'error');
      expect(screen.getByTestId('snackbar')).toHaveTextContent(
        'There was an error getting candidate data'
      );
    });

    consoleError.mockRestore();
  });

  it('calls API with correct candidate ID', async () => {
    mockApi.getCandidateData.mockResolvedValue({ res: mockCandidateData });

    renderWithProviders(<Candidate candidateId={123} isAdminUser={true} />);

    await waitFor(() => {
      expect(mockApi.getCandidateData).toHaveBeenCalledWith(123);
    });
  });
});
